import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from '@/types';

import { customBaseQuery } from '@/lib/baseQuery';
import {
  PropertyDetailPayload,
  RefinanceSteps,
  StepFormResponse,
  PersonalDetailPayload,
  IncomeDetailPayload,
  YourRefinanceDetailPayload,
} from '../types/Refinance';
import {
  mapObjectKeysToCamelCase,
  mapObjectKeysToSnakeCase,
} from '@/utils/mapper';

export const refinanceAPI = createApi({
  reducerPath: 'refinanceAPI',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getRefinanceRequestList: builder.query<
      ApiResponse<StepFormResponse[]>,
      void
    >({
      query: () => `api/refinance/v1/assessment/list`,
    }),
    postRefinanceRequest: builder.mutation<
      ApiResponse<any>,
      | IncomeDetailPayload
      | PersonalDetailPayload
      | PropertyDetailPayload
      | YourRefinanceDetailPayload
    >({
      query: ({ application_id, step, data, draft, broker_id }) => {
        const transformedData = mapObjectKeysToSnakeCase(data);
        return {
          url: `api/refinance/v1/step/`,
          method: 'POST',
          body: {
            application_id,
            step,
            draft,
            data: transformedData,
            broker_id,
          },
        };
      },
    }),
    getRefinanceStepDetail: builder.query<
      ApiResponse<StepFormResponse>,
      { application_id: string; step: RefinanceSteps }
    >({
      query: ({ application_id, step }) => ({
        url: `api/refinance/v1/step/${application_id}/${step}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<any>) => {
        response.data.data = mapObjectKeysToCamelCase(response.data.data);
        return response;
      },
    }),
    getRefinanceAssessmentDetail: builder.query<
      ApiResponse<any>,
      { application_id: string }
    >({
      query: ({ application_id }) =>
        `api/refinance/v1/assessment/${application_id}`,
    }),
    getRefinanceAssessmentSummary: builder.query<
      ApiResponse<any>,
      { application_id: string }
    >({
      query: ({ application_id }) =>
        `api/refinance/v1/snapshot/${application_id}`,
    }),
  }),
});

export const {
  useGetRefinanceRequestListQuery,
  usePostRefinanceRequestMutation,
  useGetRefinanceStepDetailQuery,
  useGetRefinanceAssessmentDetailQuery,
  useGetRefinanceAssessmentSummaryQuery,
} = refinanceAPI;
